/**
 * Pure rules behind the Customize drawer, so they can be tested directly:
 * at least one column stays visible, summary totals stay within the
 * definition's kpi_limits, toggled-on columns land in catalog order, and
 * drag-to-reorder (or the phone arrows) moves one item.
 */
import { ReportMetricDef } from "../../../../types/reportsV2";
import { insertInCatalogOrder } from "../model/metricCatalog";

export interface KpiLimits {
  min: number;
  max: number;
}

/** Show / hide a column. The last visible column can't be hidden. */
export function toggleColumn(
  columns: string[],
  key: string,
  catalog: ReportMetricDef[],
): string[] {
  if (columns.includes(key)) {
    return columns.length > 1 ? columns.filter((k) => k !== key) : columns;
  }
  return insertInCatalogOrder(catalog, columns, key);
}

/** "Select all" / "Deselect all" for one section's (available) metrics.
 *  Deselecting is a no-op when it would hide every column. */
export function setSectionColumns(
  columns: string[],
  sectionKeys: string[],
  on: boolean,
  catalog: ReportMetricDef[],
): string[] {
  if (on) {
    return sectionKeys.reduce(
      (acc, key) => insertInCatalogOrder(catalog, acc, key),
      columns,
    );
  }
  const rest = columns.filter((k) => !sectionKeys.includes(k));
  return rest.length > 0 ? rest : columns;
}

/** Add / remove a summary total within the definition's limits. */
export function toggleKpi(
  kpis: string[],
  key: string,
  { min, max }: KpiLimits,
): string[] {
  if (kpis.includes(key)) {
    return kpis.length > min ? kpis.filter((k) => k !== key) : kpis;
  }
  return kpis.length < max ? [...kpis, key] : kpis;
}

/** Add a group-by at the end of the nesting, or remove it. */
export function toggleGroup(groups: string[], key: string): string[] {
  return groups.includes(key)
    ? groups.filter((k) => k !== key)
    : [...groups, key];
}

/** Move `activeId` to where `overId` is (dnd-kit drag end). */
export function moveItem(
  list: string[],
  activeId: string,
  overId: string,
): string[] {
  const from = list.indexOf(activeId);
  const to = list.indexOf(overId);
  if (from === -1 || to === -1 || from === to) return list;
  const next = [...list];
  next.splice(from, 1);
  next.splice(to, 0, activeId);
  return next;
}

/** Move `id` one step up (-1) or down (+1); phones reorder with arrows. */
export function moveBy(list: string[], id: string, delta: -1 | 1): string[] {
  const from = list.indexOf(id);
  const to = from + delta;
  if (from === -1 || to < 0 || to >= list.length) return list;
  const next = [...list];
  next.splice(from, 1);
  next.splice(to, 0, id);
  return next;
}
